import type {
  ILabAccountLabManagementColors,
  TLabAccountColorTheme,
} from '@team-milva/lab-models/labAccount'
import { ENV } from './host'

/**
 * The brand tokens a custom colour writes, inline on `<html>`.
 *
 * `:root` and `document.documentElement` are the same element, so an inline property
 * here outranks both the `html[data-theme="blue"]` block in base.css and the design
 * system's own `:root` defaults. That is what lets a custom colour layer on top of the
 * chosen preset instead of replacing it: clearing these properties falls straight back
 * to green or blue, with nothing to undo.
 */
const CUSTOM_COLOR_PROPERTIES = [
  '--brand-primary',
  '--brand-on-primary',
  '--brand-nav-active-bg',
  '--brand-nav-active-text',
  '--color-action-hover',
  '--color-action-pressed',
  '--color-action-soft-bg',
  '--color-action-soft-text',
  '--color-border-focus',
] as const

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/

const WHITE = 255
const BLACK = 0

function channel(hexColor: string, index: number): number {
  return parseInt(hexColor.slice(index, index + 2), 16)
}

/**
 * The brand colour moved `percent` of the way towards white or black. Plain sRGB, the
 * same arithmetic a design tool's tint/shade does — it has to run in TS rather than as
 * CSS `color-mix` because the picker needs a real `#rrggbb` to show as its suggestion.
 */
function mixHex(brandColor: string, towards: number, percent: number): string {
  const ratio = percent / 100
  const mixed = (index: number): string =>
    Math.round(channel(brandColor, index) * (1 - ratio) + towards * ratio)
      .toString(16)
      .padStart(2, '0')

  return `#${mixed(1)}${mixed(3)}${mixed(5)}`
}

const LIGHT_TEXT = '#ffffff'
const DARK_TEXT = '#1a1a1a'

/** WCAG 2.1 normal-text minimum. */
const MIN_TEXT_CONTRAST = 4.5

/** WCAG relative luminance — sRGB channels linearized, not the raw values. */
function relativeLuminance(hexColor: string): number {
  const linear = (index: number): number => {
    const value = channel(hexColor, index) / 255

    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
  }

  return 0.2126 * linear(1) + 0.7152 * linear(3) + 0.0722 * linear(5)
}

function contrastRatio(one: string, other: string): number {
  const first = relativeLuminance(one)
  const second = relativeLuminance(other)

  return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05)
}

/**
 * White or near-black on the brand colour, whichever actually contrasts better. A plain
 * lightness threshold gets this wrong on saturated mid-tones: #ff6a00 reads as "light
 * enough for white" while white only reaches 2.87:1 there and near-black reaches 6.06:1.
 */
function getReadableTextColor(backgroundColor: string): string {
  return contrastRatio(backgroundColor, DARK_TEXT) >=
    contrastRatio(backgroundColor, LIGHT_TEXT)
    ? DARK_TEXT
    : LIGHT_TEXT
}

/**
 * The preferred colour if it is readable on that background, otherwise the same hue
 * darkened until it is. The nav background is always a light tint of the brand colour,
 * so darkening always converges — a near-white brand colour would otherwise give grey
 * nav text on an almost-white background, and the soft chips inherit the same pair.
 */
function readableOn(backgroundColor: string, preferred: string): string {
  if (contrastRatio(backgroundColor, preferred) >= MIN_TEXT_CONTRAST) return preferred

  for (let percent = 10; percent < 100; percent += 10) {
    const darker = mixHex(preferred, BLACK, percent)

    if (contrastRatio(backgroundColor, darker) >= MIN_TEXT_CONTRAST) return darker
  }

  return DARK_TEXT
}

/**
 * What the other three colours should be for a given brand colour. Only a starting
 * point: the picker prefills each field with these and the developer can overwrite any
 * of them, and whatever is left empty is resolved back through here when the theme is
 * applied. One definition, so a suggestion and a fallback can never drift apart.
 */
export function suggestCustomColors(
  brandColor: string
): ILabAccountLabManagementColors {
  const navBackground = mixHex(brandColor, WHITE, 85)

  return {
    ctaBackgroundColor: brandColor,
    ctaTextColor: getReadableTextColor(brandColor),
    activeMenuItemBackgroundColor: navBackground,
    activeMenuItemTextColor: readableOn(
      navBackground,
      mixHex(brandColor, BLACK, 25)
    ),
  }
}

function resolve(
  stored: Partial<ILabAccountLabManagementColors> | null | undefined,
  suggested: ILabAccountLabManagementColors,
  field: keyof ILabAccountLabManagementColors
): string {
  const value = stored?.[field]?.trim() ?? ''

  return HEX_COLOR.test(value) ? value : suggested[field]
}

/**
 * Apply the lab's colour theme: the chosen preset, plus the developer-only custom
 * colours on top of it when the lab has them. Called on every path that resolves a lab
 * account.
 */
export function applyColorTheme(
  colorTheme: TLabAccountColorTheme | undefined,
  customColors?: Partial<ILabAccountLabManagementColors> | null
): void {
  const root = document.documentElement
  root.setAttribute('data-theme', colorTheme ?? 'green')

  // Prod wears the preset and nothing else, however a lab account came by a colour:
  // picking one is a dev and stage affordance, and a demo colour must never ride a
  // promote into production.
  const brandColor = ENV.isProd
    ? ''
    : (customColors?.ctaBackgroundColor?.trim() ?? '')

  // No brand colour means "use the preset" — the other three are only ever modifiers of
  // it. Malformed is checked rather than trusted, since a bad value would otherwise be
  // written straight into the brand layer.
  if (!HEX_COLOR.test(brandColor)) {
    for (const property of CUSTOM_COLOR_PROPERTIES) {
      root.style.removeProperty(property)
    }

    return
  }

  const suggested = suggestCustomColors(brandColor)
  const navBackground = resolve(
    customColors,
    suggested,
    'activeMenuItemBackgroundColor'
  )
  const navText = resolve(customColors, suggested, 'activeMenuItemTextColor')
  const textColor = resolve(customColors, suggested, 'ctaTextColor')

  // The button keeps its foreground through hover and pressed (BaseButton only swaps the
  // background), so the states have to move *away* from the text rather than always
  // darkening: on #ff6a00 with near-black text, shading dropped hover to 4.49:1 and
  // pressed to 3.23:1, while tinting lifts them above the resting 6.06:1.
  const awayFromText =
    relativeLuminance(textColor) < relativeLuminance(brandColor) ? WHITE : BLACK

  root.style.setProperty('--brand-primary', brandColor)
  root.style.setProperty('--brand-on-primary', textColor)
  root.style.setProperty('--brand-nav-active-bg', navBackground)
  root.style.setProperty('--brand-nav-active-text', navText)

  // Soft chips follow the nav pair the developer picked, exactly as the blue preset uses
  // one colour for both. Hover, pressed and the focus ring have no stored field at all
  // and stay derived -- nobody hand-picks a pressed state for a demo.
  root.style.setProperty('--color-action-soft-bg', navBackground)
  root.style.setProperty('--color-action-soft-text', navText)
  root.style.setProperty(
    '--color-action-hover',
    mixHex(brandColor, awayFromText, 15)
  )
  root.style.setProperty(
    '--color-action-pressed',
    mixHex(brandColor, awayFromText, 30)
  )
  root.style.setProperty(
    '--color-border-focus',
    mixHex(brandColor, WHITE, 45)
  )
}
